import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { AnalyticEvent, AnalyticsBloc, AnalyticsData } from '@kanalytics';
import { FirstVisitBloc } from '@kbloc';
import { SharedConstants, WINDOW } from '@kservice';
import { FirstVisitCase } from '@ktypes/enums';
import { JsonObject } from '@ktypes/models';
import { StringUtil } from '@kutil';

@Component({
  selector: 'kui-modal',
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
})
export class ModalComponent implements OnChanges, OnInit, AfterViewInit, OnDestroy {
  @Input() analyticsData: AnalyticsData;
  @Input() canDismiss = true;
  @Input() closeFocus: HTMLElement;
  @Input() describedById = 'describedById';
  @Input() reverseClose = false;
  /** HTML content to show. */
  @Input() html: string = null;
  // Two-way data binding. Use `[(isOpen)]="some component property"` to open/close
  // this modal based off of an internal variable in the parent host component.
  @Input() isOpen = false;
  @Input() labelValue = 'labelValue';
  /**
   * Available modal types: (TODO: use enum?)
   * - detail (default)
   * - info
   * - legal
   * - popover
   * - form
   * - photo
   */
  @Input() type = 'detail';

  @Output() isOpenChange = new EventEmitter<boolean>();
  @Output() modalCloseMethod = new EventEmitter<string>();

  modalClasses: JsonObject = {};
  overlayClasses: JsonObject = {};
  parsedHtml: string;
  actionChoice = SharedConstants.actionChoice;

  private _focusableElements: HTMLElement[];
  private _blurListener: () => void;
  private _scrollY;

  // Because we are running transitions, we want to turn these
  // classes off and on at different times.
  @HostBinding('class.visible') visible = false;
  @HostBinding('class.active') active = false;

  @HostBinding('class.content-flex')
  @Input()
  contentFlex = false;

  @HostListener('window:keydown.escape', ['$event']) triggerEsc(event: KeyboardEvent) {
    this.closeModal(this.actionChoice.clickOutsideModal);
  }

  constructor(
    private _analyticsBloc: AnalyticsBloc,
    @Inject(DOCUMENT) private _document: Document,
    private _firstVisit: FirstVisitBloc,
    private _modalElement: ElementRef,
    private _renderer: Renderer2,
    @Inject(WINDOW) private _window: Window
  ) {}

  ngOnInit() {
    // Directly update DOM to preventing scrolling
    this._scrollY = this._window.scrollY;
    this._document.documentElement.style.top = `-${this._scrollY}px`;
    this._renderer.addClass(this._document.documentElement, 'no-scroll');
    this.modalClasses = {
      'content-container': ['legal', 'detail'].includes(this.type),
      'full-detail-container': this.type === 'detail',
      'info-container': ['info', 'photo'].includes(this.type),
      'form-container': this.type === 'form',
      'app-switcher-popover': this.type === 'popover',
      'photo-container': this.type === 'photo',
    };
    // didn't end up using overlayClasses, but leaving here for future use - delete this comment on first use - MB
    this.overlayClasses = {};
    if (this.html) {
      this.parsedHtml = StringUtil.contentAsParsedHtml(this.html);
    }
    this._firstVisit.updateFirstVisit(FirstVisitCase.lastModalLaunch, new Date().toISOString());
  }

  ngAfterViewInit() {
    this._setupFocusRotation();
  }

  ngOnDestroy() {
    // Cleanup DOM changes for preventing scrolling; restore scroll position
    this._renderer.removeClass(this._document.documentElement, 'no-scroll');
    this._scrollY = this._document.documentElement.style.top;
    this._document.documentElement.style.top = '';
    this._window.scrollTo(0, parseInt(this._scrollY || '0', 10) * -1);

    if (this._focusableElements && this._focusableElements.length > 0) {
      // remove listener
      this._blurListener();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.isOpen) {
      if (changes.isOpen.currentValue) {
        this.visible = true;
        this.active = true;
        if (this.analyticsData) {
          this._analyticsBloc.sendEvent(
            this._analyticsBloc.createEventFromInteraction({
              page: this.analyticsData.page,
              event: AnalyticEvent.help_dialog_opened,
              meta: this.analyticsData.meta,
            })
          );
        }
      } else {
        this.isOpen = false;
        this.visible = false;
        setTimeout(() => {
          this.active = false;
        }, 400); // value must match the transition duration in modal.component.scss
      }
    }
  }

  updateFocusRotation() {
    this._setupFocusRotation();
  }

  private _setupFocusRotation() {
    this._focusableElements = this._modalElement.nativeElement.querySelectorAll(
      'input, select, textarea, a[href], button, [tabindex]:not([tabindex="-1"]), audio[controls], video[controls], [contenteditable]:not([contenteditable="false"])'
    );
    const numFocusableElements = this._focusableElements && this._focusableElements.length;
    if (numFocusableElements > 0) {
      this._focusableElements[0].focus();
      this._blurListener = this._renderer.listen(this._focusableElements[numFocusableElements - 1], 'blur', () => {
        if (this._focusableElements && this._focusableElements.length > 0) {
          // on blur of the last focusable element, return to the first one
          this._focusableElements[0].focus();
        }
      });
    }
  }

  closeModal(actionChoice: string) {
    if (this.closeFocus && typeof this.closeFocus.focus === 'function') {
      this.closeFocus.focus();
    }

    if (this.analyticsData) {
      this._analyticsBloc.sendEvent(
        this._analyticsBloc.createEventFromInteraction({
          page: this.analyticsData.page,
          event: AnalyticEvent.help_dialog_closed,
          meta: this.analyticsData.meta,
        })
      );
    }

    // Tell the parent host component to change their internal variable that
    // binds to `this.isOpen`, which will also cause `this.isOpen` to be set to
    // `false`.
    if (this.canDismiss) {
      this.isOpenChange.emit(false);
      this.modalCloseMethod.emit(actionChoice);
    }
  }
}
